import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faDisplay } from "@fortawesome/pro-light-svg-icons"
import {
  faChartSimple,
  faChevronDown,
  faChevronUp,
  faDollarSign,
  faLocationDot
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Divider, ThemeProvider, Typography } from "@mui/material"
import { IThemeProps } from "app/components/App"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getStore } from "app/store/store"
import { getCompactFormattedNumber } from "app/tools/number"
import { IMapForecastScreen, IMapForecastScreenData, MAP_MAX_SCREEN_IDS } from "campaign/model"
import { CURRENCIES } from "currency/model"
import { convertValueIntoCurrency } from "currency/tools/common"
import { getEnvironmentNameFromId } from "environments/tools/common"
import { getEcpm } from "form/inventory/tools/common"
import React from "react"
import { Provider, connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { StreetViewButton } from "ui/components/StreetViewButton"
import { isPhoneScreen } from "ui/tools/responsive"
import { ScreenDetailTable } from "./ScreenDetailTable"

interface IScreenDetailsConnectProps extends IThemeProps {
  isPhone: boolean
}

interface IScreenDetailsProps {
  forecast: IMapForecastScreen
  currencyId: CURRENCIES
}

interface IScreenInfoProps {
  isPhone: boolean
  count: number
  label: string
  icon: string | IconDefinition
}

interface IScreenDataProps {
  latitude: number
  longitude: number
  data: IMapForecastScreenData
}

const ScreenData = React.memo(({ latitude, longitude, data: { screen_name, venue_type_ids } }: IScreenDataProps) => {
  const envIds: number[] = Array.isArray(venue_type_ids)
    ? venue_type_ids
    : typeof venue_type_ids === "string" && venue_type_ids
      ? (venue_type_ids as any).split("|")
      : []
  return (
    <>
      <FlexBox
        ai="start"
        vertical
        fullWidth
        sx={{
          "& .icon": { color: theme => theme.palette.text.primary2 },
          "& .MuiDivider-root": {
            width: "calc(100% - 2rem)",
            ml: "1rem"
          }
        }}>
        {screen_name && (
          <FlexBox vertical fullWidth jc="flex-start" ai="flex-start" gap=".4rem" sx={{ maxWidth: "20rem" }}>
            <FlexBox fullWidth jc="space-between">
              <FlexBox sx={{ gap: ".5rem" }} fullWidth jc="flex-start" ai="center">
                <FontAwesomeIcon icon={faDisplay} className="icon" size="2x" />
                <Typography
                  fontSize=".9rem"
                  width="100%"
                  fontWeight="600"
                  maxWidth="18rem"
                  maxHeight="3rem"
                  sx={{
                    color: theme => theme.palette.text.primary2,
                    wordWrap: "break-word"
                  }}>
                  {screen_name}
                </Typography>
              </FlexBox>
              <StreetViewButton latitude={latitude} longitude={longitude} size="2x" />
            </FlexBox>
            <Divider sx={{ width: "100%" }} />
          </FlexBox>
        )}
        {envIds.length > 0 && (
          <>
            <FlexBox sx={{ gap: ".5rem", mt: ".2rem", mb: ".2rem", maxWidth: "20rem" }}>
              <FontAwesomeIcon icon={faLocationDot} width="1rem" className="icon" size="2x" />
              <Typography fontSize=".75rem" sx={{ color: theme => theme.palette.text.primary2 }}>
                {envIds.map(id => getEnvironmentNameFromId(Number(id))).join(", ")}
              </Typography>
            </FlexBox>
          </>
        )}
      </FlexBox>
    </>
  )
})

const ScreenInfo = React.memo(({ count, label, icon, isPhone }: IScreenInfoProps) => (
  <FlexBox
    jc={isPhone ? "start" : undefined}
    sx={{
      "width": isPhone ? "100%" : undefined,
      "& .screen-icon": {
        mr: ".5rem",
        color: theme => theme.palette.text.primary2,
        minWidth: isPhone ? "2rem" : undefined
      }
    }}>
    {typeof icon === "string" ? (
      <Typography fontSize={isPhone ? ".75rem" : "1.3rem"} className="screen-icon">
        {icon}
      </Typography>
    ) : (
      <FontAwesomeIcon icon={icon} className="screen-icon" size={isPhone ? "2x" : "3x"} />
    )}
    <FlexBox vertical sx={{ zIndex: 1, alignItems: "start" }}>
      <Typography
        fontSize={isPhone ? ".75rem" : "1.3rem"}
        fontWeight="600"
        sx={{ color: theme => theme.palette.text.primary2, whiteSpace: "nowrap" }}>
        {getCompactFormattedNumber(count)}
      </Typography>
      <Typography fontSize={isPhone ? ".75rem" : ".9rem"} color="textPrimary">
        {label}
      </Typography>
    </FlexBox>
  </FlexBox>
))

const ScreenDivider = ({ isPhone }: { isPhone: boolean }) => (
  <Box
    sx={{
      height: isPhone ? ".1rem" : "2rem",
      width: isPhone ? "100%" : 0,
      ml: isPhone ? undefined : ".5rem",
      mr: isPhone ? undefined : ".5rem",
      mt: isPhone ? ".5rem" : undefined,
      mb: isPhone ? ".5rem" : undefined,
      borderLeft: isPhone ? undefined : theme => `1px solid ${theme.palette.text.primary2}`,
      borderTop: isPhone ? theme => `1px solid ${theme.palette.text.primary2}` : undefined
    }}
  />
)

const ScreenDetailsFn = React.memo(
  ({ currencyId, forecast, theme, isPhone }: IScreenDetailsProps & IScreenDetailsConnectProps) => {
    const [tableIsExpanded, setTableIsExpanded] = React.useState(false)
    let singleScreenData: IMapForecastScreenData | undefined
    if (forecast.screen_count === 1 && forecast.single_screen_data) {
      try {
        singleScreenData =
          typeof forecast.single_screen_data === "string"
            ? JSON.parse(forecast.single_screen_data)
            : forecast.single_screen_data
      } catch (ex) {
        console.error(ex)
      }
    }
    const canDisplayTable = !isPhone && forecast.screen_count > 1 && forecast.screen_ids
    return (
      <ThemeProvider theme={theme}>
        <FlexBox vertical sx={{ borderRadius: ".25" }}>
          {singleScreenData && (
            <ScreenData
              latitude={forecast.centroid_latitude}
              longitude={forecast.centroid_longitude}
              data={singleScreenData}
            />
          )}
          <FlexBox
            fullWidth
            vertical={isPhone}
            jc="space-evenly"
            sx={{
              mt: ".3rem",
              mr: "1rem",
              ml: "1rem"
            }}>
            <ScreenInfo
              count={forecast.impressions}
              label={translate("label.impressions")}
              icon={faChartSimple}
              isPhone={isPhone}
            />
            <ScreenDivider isPhone={isPhone} />
            <ScreenInfo
              count={convertValueIntoCurrency(
                CURRENCIES.CAD,
                currencyId,
                getEcpm(forecast.spend, forecast.impressions)
              )}
              label={translate("label.ecpm.average")}
              icon={faDollarSign}
              isPhone={isPhone}
            />
            {forecast.screen_count !== 1 && (
              <>
                <ScreenDivider isPhone={isPhone} />
                <ScreenInfo
                  count={forecast.screen_count}
                  label={translate("label.screens")}
                  icon={faDisplay}
                  isPhone={isPhone}
                />
              </>
            )}
          </FlexBox>
          {canDisplayTable && forecast.screen_ids && (
            <FlexBox vertical fullWidth>
              <FlexBox
                fullWidth
                gap
                sx={{
                  "cursor": "pointer",
                  "height": "2rem",
                  "borderTop": theme => theme.palette.border,
                  "mt": ".5rem",
                  //"borderBottom": tableIsExpanded ? theme => theme.palette.border : undefined,
                  "marginBottom": tableIsExpanded ? undefined : "-.8rem",
                  "& .icon": {
                    color: theme => theme.palette.text.primary
                  }
                }}
                onClick={() => setTableIsExpanded(!tableIsExpanded)}
                jc="end">
                <Typography color="textPrimary" fontSize=".8rem">
                  {translate("label.impressions.top.10", MAP_MAX_SCREEN_IDS)}
                </Typography>
                <FontAwesomeIcon icon={tableIsExpanded ? faChevronDown : faChevronUp} size="1x" className="icon" />
              </FlexBox>
              <FlexBox
                sx={{
                  overflow: "hidden",
                  transition: "all 300ms ease",
                  height: tableIsExpanded ? "14rem" : 0,
                  minHeight: tableIsExpanded ? "14rem" : 0,
                  maxHeight: tableIsExpanded ? "14rem" : 0
                }}>
                <ScreenDetailTable
                  screen_ids={forecast.screen_ids}
                  screen_impressions={forecast.screen_impressions || []}
                  screen_spends={forecast.screen_spends || []}
                />
              </FlexBox>
            </FlexBox>
          )}
        </FlexBox>
      </ThemeProvider>
    )
  }
)

export const ScreenDetailsConnect = connect(
  (state: IRootState): IScreenDetailsConnectProps => ({
    theme: state.ui.theme,
    isPhone: isPhoneScreen(state)
  })
)(ScreenDetailsFn)

export const ScreenDetails = React.memo(({ forecast, currencyId }: IScreenDetailsProps) => (
  <Provider store={getStore()}>
    <ScreenDetailsConnect forecast={forecast} currencyId={currencyId} />
  </Provider>
))
